import { Rate } from '~/services/AccommodationService';

enum BundleStatus {
  /** `!packaged && !isFlightBundle` */
  NOT_BUNDLED,
  /** `!packaged && isFlightBundle` */
  MISCONFIGURED,
  /** `packaged && !isFlightBundle` */
  FAUX_NOT_BUNDLED,
  /** `packaged && isFlightBundle` */
  BUNDLED,
}

interface BundleStatusInfo {
  summary: string;
  description: string;
  color: 'error' | 'warning' | 'info';
}

const BUNDLE_STATUS_TO_INFO: Record<BundleStatus, BundleStatusInfo | undefined> = {
  [BundleStatus.NOT_BUNDLED]: undefined,
  [BundleStatus.MISCONFIGURED]: {
    summary: 'No',
    description: 'This rate does not include a flight bundle, but will only be shown on the "Hotels + flights" tab',
    color: 'error',
  },
  [BundleStatus.FAUX_NOT_BUNDLED]: {
    summary: 'Yes',
    description: 'This rate includes a flight bundle, but will also be shown on the "Hotels" tab',
    color: 'warning',
  },
  [BundleStatus.BUNDLED]: {
    summary: 'Yes',
    description: 'This rate includes a flight bundle, and will only be shown on the "Hotels + flights" tab',
    color: 'info',
  },
};

export const getBundleStatusInfo = (meta: Rate['meta']) => {
  const packaged = meta?.packaged ?? false;
  const isFlightBundle = meta?.isFlightBundle ?? false;

  const packagedOffset = packaged ? 2 : 0;
  const isFlightBundleOffset = isFlightBundle ? 1 : 0;
  const bundleStatus: BundleStatus = packagedOffset + isFlightBundleOffset;
  return BUNDLE_STATUS_TO_INFO[bundleStatus];
};

export const getExternalId = (rate: Rate) => {
  if (rate.meta.type === 'bedbank') {
    return rate.meta.bedbankRateId;
  }
  return rate.meta.reservationRoomRateId;
};

/**
 * Splits a rate's supplier surcharges into the part we collect and the part the property
 * collects directly — in practice the extra guest surcharge.
 *
 * `reservationRateToUnifiedRate` folds the collected part into `total.sellingPrice` and leaves
 * the property-collected part out of it, putting only the latter on `total.displayPrice`. So
 * "payable at property" on a rate is `total.propertyFees` plus `payableAtProperty` surcharges,
 * and showing property fees alone understates it. Bedbank rates always return `[]`.
 */
export const getSurchargeSplit = (rate: Pick<Rate, 'surcharges'>) => {
  let payableAtProperty = 0;
  let collectedByUs = 0;
  for (const surcharge of rate.surcharges ?? []) {
    if (surcharge.payableAtProperty) {
      payableAtProperty += surcharge.sell;
    } else {
      collectedByUs += surcharge.sell;
    }
  }
  return { payableAtProperty, collectedByUs };
};
